import Button from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardCloseButton,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Textarea from "@/components/ui/textarea";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";
import { useUpdateSlideMutation } from "@api/slides-library";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";

const DIALOG_ID = "slide-when-to-use-dialog";

// Long enough for a real instruction, short enough that the generator's menu
// line stays readable when every layout in the library carries one.
const MAX_LENGTH = 480;

/**
 * Curator guidance for the presentation generator's layout chooser.
 *
 * The chooser filters layouts STRUCTURALLY (can this shape hold the page's
 * content?) and then has to break ties between several layouts that fit
 * equally well. Titles cannot do that — nine of them are shared by two or
 * three layouts — and ids change on every republish. This sentence is the only
 * durable, human-owned statement of what a layout is FOR.
 *
 * It is saved on the library item, NEVER inside `slide`. That distinction is
 * the whole point: `slide` is the object cloned into a customer's deck, so
 * anything stored there would render. This travels into the secured layout
 * index instead, and is stripped from the public CDN copy at publish.
 *
 * Takes effect on the next layouts publish, not on save.
 */
const SlideWhenToUseDialog = ({ slide }: { slide: TSavedSlide }) => {
  const dispatch = useAppDispatch();
  const [text, setText] = useState(slide.whenToUse ?? "");
  const [updateSlide, { isLoading }] = useUpdateSlideMutation();

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        // Trimmed, and an empty box is a deliberate clear — the backend keys on
        // `typeof whenToUse === "string"` so "" removes the description rather
        // than being ignored as falsy.
        await updateSlide({ id: slide._id, whenToUse: text.trim() }).unwrap();
        toast.success("Saved — publish layouts for it to take effect");
        handleClose();
      },
      onError: () => toast.error("Failed to save the description"),
    });
  }, [updateSlide, handleClose, isLoading, slide._id, text]);

  const unchanged = text.trim() === (slide.whenToUse ?? "").trim();

  return (
    <Card>
      <CardHeader>
        <CardTitle>When to use this layout</CardTitle>
        <CardDescription className="tw-text-blue-700">
          Describe the kind of page this layout is for. The generator reads it
          when several layouts fit a page equally well.
          <br />
          Only your team sees this - it never appears in a story.
        </CardDescription>
        <CardAction>
          <CardCloseButton onClick={handleClose} />
        </CardAction>
      </CardHeader>

      <CardContent className="tw-flex tw-flex-col tw-gap-1">
        <Textarea
          aria-label="When to use this layout"
          placeholder="e.g. A team page - one portrait and a name per person."
          value={text}
          maxLength={MAX_LENGTH}
          className="tw-h-32 tw-font-sans tw-text-base tw-font-normal"
          onChange={(e) => setText(e.target.value)}
        />
        <span className="tw-block tw-text-right tw-font-sans tw-text-[11px]/tight tw-font-normal tw-text-blue-500">
          {text.length}/{MAX_LENGTH}
        </span>
      </CardContent>

      <CardFooter className="tw-justify-end tw-gap-4">
        <Button variant="secondary" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={unchanged}
          isLoading={isLoading}
        >
          {isLoading ? "Saving..." : "Save changes"}
        </Button>
      </CardFooter>
    </Card>
  );
};

SlideWhenToUseDialog.id = DIALOG_ID;
export { SlideWhenToUseDialog };
